import { motion } from 'framer-motion';
import { Briefcase, GraduationCap, Palette, Home } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { OnboardingStep, FadeUp, ScaleIn } from './OnboardingStep';
import { cn } from '@/lib/utils';

interface FocusOption {
  id: string;
  label: string;
  icon: typeof Briefcase;
  color: string;
}

const focusOptions: FocusOption[] = [
  { id: 'Work', label: 'Work', icon: Briefcase, color: '#3b82f6' },
  { id: 'Student', label: 'Student', icon: GraduationCap, color: '#a855f7' },
  { id: 'Creative', label: 'Creative', icon: Palette, color: '#ec4899' },
  { id: 'Personal', label: 'Personal', icon: Home, color: '#22c55e' },
];

interface StepIntentionProps {
  selectedFocus: string | null;
  setSelectedFocus: (focus: string) => void;
  onNext: () => void;
}

export function StepIntention({ selectedFocus, setSelectedFocus, onNext }: StepIntentionProps) {
  return (
    <OnboardingStep>
      <FadeUp delay={0.2}>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-3">
          What is your main focus
        </h1>
      </FadeUp>
      
      <FadeUp delay={0.4}>
        <p className="text-3xl sm:text-4xl font-bold tracking-tight text-muted-foreground mb-16">
          right now?
        </p>
      </FadeUp>
      
      <ScaleIn delay={0.6} className="w-full">
        <div className="grid grid-cols-2 gap-4 mb-14">
          {focusOptions.map((option, index) => {
            const Icon = option.icon;
            const isSelected = selectedFocus === option.id;
            
            return (
              <motion.button
                key={option.id}
                onClick={() => setSelectedFocus(option.id)}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.7 + index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={cn(
                  "relative flex flex-col items-center justify-center p-6 rounded-2xl border-2 transition-all duration-300",
                  "bg-background/30 backdrop-blur-sm",
                  isSelected 
                    ? "border-primary shadow-lg" 
                    : "border-border/30 hover:border-border/60"
                )}
                style={{
                  boxShadow: isSelected ? `0 0 30px ${option.color}30` : undefined,
                }}
              >
                <div 
                  className={cn(
                    "w-12 h-12 rounded-xl flex items-center justify-center mb-3 transition-colors",
                    isSelected ? "bg-primary/20" : "bg-muted/50"
                  )}
                  style={{
                    backgroundColor: isSelected ? `${option.color}20` : undefined,
                  }}
                >
                  <Icon 
                    className="w-6 h-6 transition-colors" 
                    style={{ color: isSelected ? option.color : 'hsl(var(--muted-foreground))' }}
                  />
                </div>
                <span className={cn(
                  "text-base font-medium transition-colors",
                  isSelected ? "text-foreground" : "text-muted-foreground"
                )}>
                  {option.label}
                </span>
                
                {isSelected && (
                  <motion.div
                    layoutId="focus-indicator"
                    className="absolute inset-0 rounded-2xl border-2 border-primary"
                    initial={false}
                    transition={{ type: "spring", stiffness: 500, damping: 30 }}
                  />
                )}
              </motion.button>
            );
          })}
        </div>
      </ScaleIn>
      
      <ScaleIn delay={1.1} className="w-full mt-4">
        <Button
          size="lg"
          disabled={!selectedFocus}
          onClick={onNext}
          className="w-full h-14 text-lg font-medium"
        >
          Continue
        </Button>
      </ScaleIn>
    </OnboardingStep>
  );
}

export { focusOptions };
